/* =========================================================
 * Masterpiece Mirrors — Custom Cut-to-Size Landing Page
 * Calculator + FAQ accordion.
 *
 * Price = material (area x rate per m2) + edging (perimeter x rate per lm),
 * floored at the per-type minimum, plus vinyl backing if selected.
 * Pricing rates and the per-type minimum prices are read from data-*
 * attributes on #mirror-calculator, which are populated by
 * the section schema in sections/static-custom-mirror.liquid.
 * ========================================================= */
(function () {
  function init() {
    initCalculator();
    initFaqAccordion();
  }

  /* -------------------- Price Calculator -------------------- */
  function initCalculator() {
    var calculator = document.getElementById('mirror-calculator');
    if (!calculator) return;

    var widthInput         = document.getElementById('width');
    var heightInput        = document.getElementById('height');
    var thicknessSelector  = document.getElementById('thickness-selector');
    var thicknessInput     = document.getElementById('thickness');
    var finishSelector     = document.getElementById('finish-selector');
    var bevelWrap          = document.getElementById('bevel-width-wrap');
    var bevelInput         = document.getElementById('bevel-width');
    var bevelNote          = document.getElementById('bevel-width-note');
    var thicknessNote      = document.getElementById('thickness-note');
    var priceDisplay       = document.getElementById('estimated-price');
    var mirrorTypeNote     = document.getElementById('mirror-type-note');
    var mirrorTypeSelector = document.getElementById('mirror-type-selector');
    var vinylSelector      = document.getElementById('vinyl-selector');

    // Inputs on the quote-request form further down the page
    var quoteWidth       = document.getElementById('quote-width');
    var quoteHeight      = document.getElementById('quote-height');
    var quoteThickness   = document.getElementById('quote-thickness');
    var quoteEdge        = document.getElementById('quote-edge');
    var quoteBevelWrap   = document.getElementById('quote-bevel-wrap');
    var quoteBevelWidth  = document.getElementById('quote-bevel-width');
    var quoteMirrorType  = document.getElementById('quote-mirror-type');

    // Material rates (per square metre, inc. GST) — injected from schema.
    // NOTE: Attribute names avoid hyphen-before-digit (e.g. data-rate-silver4,
    // not data-rate-silver-4mm) because the HTML5 dataset API only collapses
    // `-` + lowercase-letter into camelCase, so `-4` would survive as a
    // literal hyphen in the key and the dot-accessor would return undefined.
    var RATES = {
      silver: {
        '3mm': parseFloat(calculator.dataset.rateSilver3) || 0,
        '4mm': parseFloat(calculator.dataset.rateSilver4) || 0,
        '6mm': parseFloat(calculator.dataset.rateSilver6) || 0
      },
      grey: {
        '3mm': parseFloat(calculator.dataset.rateGrey6) || 0,   // 3mm/4mm not offered; fallback to 6mm rate
        '4mm': parseFloat(calculator.dataset.rateGrey6) || 0,
        '6mm': parseFloat(calculator.dataset.rateGrey6) || 0
      },
      bronze: {
        '3mm': parseFloat(calculator.dataset.rateBronze6) || 0, // 3mm/4mm not offered; fallback to 6mm rate
        '4mm': parseFloat(calculator.dataset.rateBronze6) || 0,
        '6mm': parseFloat(calculator.dataset.rateBronze6) || 0
      }
    };
    // Edging rates (per linear metre of perimeter, inc. GST). The bevelled
    // rate is a flat rate whatever the bevel width (8mm to 25mm).
    var EDGE_RATES = {
      'flat-polished': parseFloat(calculator.dataset.edgeFlatPolished) || 0,
      'bevelled':      parseFloat(calculator.dataset.edgeBevelled) || 0
    };
    // Minimum price per mirror type (silver differs from the tinted mirrors).
    // Applied to the combined material + edging price.
    var MINIMUM_PRICES = {
      silver: parseFloat(calculator.dataset.minimumPrice) || 0,
      grey:   parseFloat(calculator.dataset.minimumPriceGrey) || 0,
      bronze: parseFloat(calculator.dataset.minimumPriceBronze) || 0
    };
    var VINYL_RATE    = parseFloat(calculator.dataset.vinylRate) || 0;

    var BEVEL_MIN = parseFloat(bevelInput && bevelInput.min) || 8;
    var BEVEL_MAX = parseFloat(bevelInput && bevelInput.max) || 25;

    var selectedMirrorType = 'silver';
    var selectedEdge       = 'flat-polished';
    var vinylBacking       = false;

    // Returns the bevel width as a number when it is valid (8 to 25), otherwise null.
    function validBevelWidth() {
      if (!bevelInput) return null;
      var v = parseFloat(bevelInput.value);
      if (isNaN(v) || v < BEVEL_MIN || v > BEVEL_MAX) return null;
      return v;
    }

    function updateBevelNote() {
      if (!bevelNote || !bevelInput) return;
      var raw = bevelInput.value;
      var outOfRange = raw !== '' && validBevelWidth() === null;
      bevelNote.classList.toggle('text-red-500', outOfRange);
      bevelNote.classList.toggle('text-gray-500', !outOfRange);
      bevelNote.textContent = outOfRange
        ? 'The bevel width must be between ' + BEVEL_MIN + 'mm and ' + BEVEL_MAX + 'mm.'
        : 'Bevel width required: please specify the bevel width you would like, between ' + BEVEL_MIN + 'mm and ' + BEVEL_MAX + 'mm.';
    }

    // Keeps the quote form in step with the calculator selections.
    function syncQuoteForm() {
      var bevelled = selectedEdge === 'bevelled';

      if (quoteWidth)  quoteWidth.value  = widthInput.value;
      if (quoteHeight) quoteHeight.value = heightInput.value;
      if (quoteThickness) quoteThickness.value = thicknessInput.value;
      if (quoteEdge)   quoteEdge.value   = selectedEdge;
      if (quoteBevelWidth && bevelled) quoteBevelWidth.value = bevelInput ? bevelInput.value : '';
      toggleQuoteBevel();
    }

    // Show (and require) the bevel width on the quote form only for bevelled edges.
    function toggleQuoteBevel() {
      if (!quoteEdge || !quoteBevelWrap) return;
      var bevelled = quoteEdge.value === 'bevelled';
      quoteBevelWrap.classList.toggle('hidden', !bevelled);
      if (quoteBevelWidth) {
        quoteBevelWidth.required = bevelled;
        if (!bevelled) quoteBevelWidth.value = '';
      }
    }

    function calculatePrice() {
      syncQuoteForm();

      var rate = RATES[selectedMirrorType][thicknessInput.value];
      var widthMM  = parseFloat(widthInput.value);
      var heightMM = parseFloat(heightInput.value);
      var widthM  = widthMM / 1000;
      var heightM = heightMM / 1000;

      if (isNaN(widthM) || isNaN(heightM) || widthM <= 0 || heightM <= 0) {
        priceDisplay.textContent = '$0.00';
        return;
      }

      var area         = widthM * heightM;
      var perimeterM   = 2 * (widthMM + heightMM) / 1000;   // linear metres
      var materialCost = area * rate;
      var edgeCost     = perimeterM * (EDGE_RATES[selectedEdge] || 0);
      var minimum      = MINIMUM_PRICES[selectedMirrorType] || 0;
      // Minimum applies to the combined material + edging price.
      var mirrorCost   = Math.max(materialCost + edgeCost, minimum);
      var vinylCost    = vinylBacking ? (area * VINYL_RATE) : 0;
      var total        = mirrorCost + vinylCost;

      priceDisplay.textContent = '$' + total.toFixed(2);
    }

    // Which thicknesses can be chosen right now:
    //  - grey / bronze are 6mm only
    //  - a bevelled edge is not available in 3mm
    // Unavailable options are dimmed in the calculator and hidden in the quote
    // form's Thickness dropdown, so an unbuildable combination can't be submitted.
    function refreshThicknessAvailability() {
      var only6mm  = (selectedMirrorType === 'grey' || selectedMirrorType === 'bronze');
      var bevelled = (selectedEdge === 'bevelled');
      var blocked = {
        '3mm': only6mm || bevelled,
        '4mm': only6mm,
        '6mm': false
      };

      var switchedFrom = null;
      if (blocked[thicknessInput.value]) {
        switchedFrom = thicknessInput.value;
        thicknessInput.value = only6mm ? '6mm' : '4mm';
      }

      ['3mm', '4mm', '6mm'].forEach(function (val) {
        var opt = thicknessSelector.querySelector('[data-value="' + val + '"]');
        if (!opt) return;
        opt.style.opacity = blocked[val] ? '0.35' : '1';
        opt.style.pointerEvents = blocked[val] ? 'none' : '';
        opt.classList.toggle('selected', val === thicknessInput.value);
      });

      if (thicknessNote) {
        var msg = '';
        if (bevelled && !only6mm) {
          msg = '3mm mirror is not available with a bevelled edge.';
          if (switchedFrom === '3mm') msg += ' Thickness has been changed to 4mm.';
        }
        thicknessNote.textContent = msg;
        thicknessNote.classList.toggle('hidden', msg === '');
      }

      if (quoteThickness) {
        ['3mm', '4mm'].forEach(function (val) {
          var o = quoteThickness.querySelector('option[value="' + val + '"]');
          if (o) { o.hidden = blocked[val]; o.disabled = blocked[val]; }
        });
        quoteThickness.value = thicknessInput.value;
      }
    }

    // Mirror-type chip selector
    if (mirrorTypeSelector) {
      mirrorTypeSelector.addEventListener('click', function (e) {
        var chip = e.target.closest('.mirror-chip');
        if (!chip) return;
        mirrorTypeSelector.querySelectorAll('.mirror-chip').forEach(function (c) {
          c.classList.remove('selected');
        });
        chip.classList.add('selected');
        selectedMirrorType = chip.dataset.type;
        if (mirrorTypeNote) mirrorTypeNote.textContent = '';
        if (quoteMirrorType) quoteMirrorType.value = selectedMirrorType;
        refreshThicknessAvailability();
        calculatePrice();
      });
    }

    // Vinyl-backing chip selector
    if (vinylSelector) {
      vinylSelector.addEventListener('click', function (e) {
        var chip = e.target.closest('.mirror-chip');
        if (!chip) return;
        vinylSelector.querySelectorAll('.mirror-chip').forEach(function (c) {
          c.classList.remove('selected');
        });
        chip.classList.add('selected');
        vinylBacking = chip.dataset.vinyl === 'yes';
        calculatePrice();
      });
    }

    // Thickness (3mm / 4mm / 6mm) selector
    if (thicknessSelector) {
      thicknessSelector.addEventListener('click', function (e) {
        var selectedOption = e.target.closest('.edge-option');
        if (!selectedOption) return;
        thicknessSelector.querySelectorAll('.edge-option').forEach(function (opt) {
          opt.classList.remove('selected');
        });
        selectedOption.classList.add('selected');
        thicknessInput.value = selectedOption.dataset.value;
        calculatePrice();
      });
    }

    // Edge finish (flat polished / bevelled) selector. Choosing bevelled
    // reveals the bevel width field (8mm to 25mm).
    if (finishSelector) {
      finishSelector.addEventListener('click', function (e) {
        var selectedOption = e.target.closest('.edge-option');
        if (!selectedOption) return;
        finishSelector.querySelectorAll('.edge-option').forEach(function (opt) {
          opt.classList.remove('selected');
        });
        selectedOption.classList.add('selected');
        selectedEdge = selectedOption.dataset.edge;
        var edgeHidden = document.getElementById('edge-finish');
        if (edgeHidden) edgeHidden.value = selectedEdge;
        if (bevelWrap) bevelWrap.classList.toggle('hidden', selectedEdge !== 'bevelled');
        if (selectedEdge !== 'bevelled' && bevelInput) bevelInput.value = '';
        refreshThicknessAvailability();
        updateBevelNote();
        calculatePrice();
      });
    }

    if (bevelInput) {
      bevelInput.addEventListener('input', function () {
        updateBevelNote();
        calculatePrice();
      });
    }

    // If the visitor changes the Edge Finish on the quote form itself,
    // show or hide its bevel width field to match.
    if (quoteEdge) {
      quoteEdge.addEventListener('change', function () {
        toggleQuoteBevel();
        // 3mm can't be bevelled, so keep the quote form's Thickness in step.
        if (!quoteThickness) return;
        var only6mm  = (selectedMirrorType === 'grey' || selectedMirrorType === 'bronze');
        var bevelled = quoteEdge.value === 'bevelled';
        var o3 = quoteThickness.querySelector('option[value="3mm"]');
        if (o3) { o3.hidden = only6mm || bevelled; o3.disabled = only6mm || bevelled; }
        if (bevelled && quoteThickness.value === '3mm') quoteThickness.value = only6mm ? '6mm' : '4mm';
      });
    }

    [widthInput, heightInput].forEach(function (el) {
      if (el) el.addEventListener('input', calculatePrice);
    });

    refreshThicknessAvailability();
    calculatePrice();
  }

  /* -------------------- FAQ Accordion -------------------- */
  function initFaqAccordion() {
    var faqContainer = document.getElementById('faq-container');
    if (!faqContainer) return;

    faqContainer.addEventListener('click', function (e) {
      var questionButton = e.target.closest('.faq-question');
      if (!questionButton) return;

      var item   = questionButton.parentElement;
      var answer = item.querySelector('.faq-answer');
      var arrow  = questionButton.querySelector('.faq-arrow');
      var isOpening = !answer.style.maxHeight || answer.style.maxHeight === '0px';

      // Close all other items first
      faqContainer.querySelectorAll('.faq-item').forEach(function (otherItem) {
        if (otherItem !== item) {
          var otherAnswer = otherItem.querySelector('.faq-answer');
          var otherArrow  = otherItem.querySelector('.faq-arrow');
          otherAnswer.style.maxHeight     = '0px';
          otherAnswer.style.paddingTop    = '0';
          otherAnswer.style.paddingBottom = '0';
          if (otherArrow) otherArrow.classList.remove('rotate-180');
        }
      });

      // Toggle the clicked item
      if (isOpening) {
        answer.style.maxHeight     = answer.scrollHeight + 'px';
        answer.style.paddingTop    = '0';
        answer.style.paddingBottom = '1.5rem';
        if (arrow) arrow.classList.add('rotate-180');
      } else {
        answer.style.maxHeight     = '0px';
        answer.style.paddingTop    = '0';
        answer.style.paddingBottom = '0';
        if (arrow) arrow.classList.remove('rotate-180');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();